import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { AppointmentService } from 'src/app/private/services/appointment.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { Chart, registerables } from 'chart.js';
import moment from 'moment';

Chart.register(...registerables);

export const DATE_PRESETS = [
  { label: 'Today',        value: 'today' },
  { label: 'Yesterday',    value: 'yesterday' },
  { label: 'Tomorrow',     value: 'tomorrow' },
  { label: 'This Week',    value: 'this_week' },
  { label: 'Next Week',    value: 'next_week' },
  { label: 'This Month',   value: 'this_month' },
  { label: 'Last Month',   value: 'last_month' },
  { label: 'This Quarter', value: 'this_quarter' },
  { label: 'Last Quarter', value: 'last_quarter' },
  { label: 'This Year',    value: 'this_year' },
  { label: 'Last Year',    value: 'last_year' },
];

@Component({
  selector: 'app-appointment-reports',
  templateUrl: './appointment-reports.component.html',
  styleUrls: ['./appointment-reports.component.scss']
})
export class AppointmentReportsComponent implements OnInit, OnDestroy {
  @ViewChild('statusChartCanvas') statusChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('trendChartCanvas') trendChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('typeChartCanvas') typeChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('doctorChartCanvas') doctorChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('centerChartCanvas') centerChartCanvas!: ElementRef<HTMLCanvasElement>;

  DATE_PRESETS = DATE_PRESETS;
  selectedPreset = 'this_month';
  dateRange: any = { from: '', to: '' };
  isCustomRange = false;
  customFrom = '';
  customTo = '';

  loading = false;
  activeTab = 'overview'; // 'overview' | 'doctor' | 'center'

  stats: any = { total: 0, checkedIn: 0, completed: 0, waiting: 0, cancelled: 0, noShow: 0 };
  doctorWiseData: any[] = [];
  centerWiseData: any[] = [];
  trendData: any[] = [];
  typeData: any[] = [];

  private statusChart: Chart | null = null;
  private trendChart: Chart | null = null;
  private typeChart: Chart | null = null;
  private doctorChart: Chart | null = null;
  private centerChart: Chart | null = null;

  constructor(
    private api: AppointmentService,
    private localStorage: LocalStorageService
  ) {}

  ngOnInit() {
    this.applyPreset(this.selectedPreset);
  }

  ngOnDestroy() {
    this.destroyCharts();
  }

  destroyCharts() {
    this.statusChart?.destroy();
    this.trendChart?.destroy();
    this.typeChart?.destroy();
    this.doctorChart?.destroy();
    this.centerChart?.destroy();
  }

  applyPreset(preset: string) {
    this.selectedPreset = preset;
    this.isCustomRange = false;
    const today = moment();

    switch (preset) {
      case 'today':
        this.dateRange = { from: today.format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') }; break;
      case 'yesterday':
        this.dateRange = { from: today.clone().subtract(1,'d').format('YYYY-MM-DD'), to: today.clone().subtract(1,'d').format('YYYY-MM-DD') }; break;
      case 'tomorrow':
        this.dateRange = { from: today.clone().add(1,'d').format('YYYY-MM-DD'), to: today.clone().add(1,'d').format('YYYY-MM-DD') }; break;
      case 'this_week':
        this.dateRange = { from: today.clone().startOf('isoWeek').format('YYYY-MM-DD'), to: today.clone().endOf('isoWeek').format('YYYY-MM-DD') }; break;
      case 'next_week':
        this.dateRange = { from: today.clone().add(1,'w').startOf('isoWeek').format('YYYY-MM-DD'), to: today.clone().add(1,'w').endOf('isoWeek').format('YYYY-MM-DD') }; break;
      case 'this_month':
        this.dateRange = { from: today.clone().startOf('month').format('YYYY-MM-DD'), to: today.clone().endOf('month').format('YYYY-MM-DD') }; break;
      case 'last_month':
        this.dateRange = { from: today.clone().subtract(1,'M').startOf('month').format('YYYY-MM-DD'), to: today.clone().subtract(1,'M').endOf('month').format('YYYY-MM-DD') }; break;
      case 'this_quarter':
        this.dateRange = { from: today.clone().startOf('quarter').format('YYYY-MM-DD'), to: today.clone().endOf('quarter').format('YYYY-MM-DD') }; break;
      case 'last_quarter':
        this.dateRange = { from: today.clone().subtract(1,'Q').startOf('quarter').format('YYYY-MM-DD'), to: today.clone().subtract(1,'Q').endOf('quarter').format('YYYY-MM-DD') }; break;
      case 'this_year':
        this.dateRange = { from: today.clone().startOf('year').format('YYYY-MM-DD'), to: today.clone().endOf('year').format('YYYY-MM-DD') }; break;
      case 'last_year':
        this.dateRange = { from: today.clone().subtract(1,'y').startOf('year').format('YYYY-MM-DD'), to: today.clone().subtract(1,'y').endOf('year').format('YYYY-MM-DD') }; break;
    }
    this.loadReportData();
  }

  applyCustomRange() {
    if (!this.customFrom || !this.customTo) return;
    this.dateRange = { from: this.customFrom, to: this.customTo };
    this.isCustomRange = true;
    this.selectedPreset = '';
    this.loadReportData();
  }

  selectTab(tab: string) {
    this.activeTab = tab;
    setTimeout(() => {
      if (tab === 'overview') {
        this.renderStatusChart();
        this.renderTrendChart();
        this.renderTypeChart();
      } else if (tab === 'doctor') {
        this.renderDoctorChart();
      } else if (tab === 'center') {
        this.renderCenterChart();
      }
    }, 80);
  }

  loadReportData() {
    this.loading = true;
    this.stats = { total: 0, checkedIn: 0, completed: 0, waiting: 0, cancelled: 0, noShow: 0 };
    this.destroyCharts();

    const param: any = {
      hospital_id: this.localStorage.getHospitalId(),
      startDate: this.dateRange.from,
      endDate: this.dateRange.to,
    };
    if (this.localStorage.getIgnoreCenterCheck() !== 'true') {
      param.center_id = this.localStorage.getCenterId();
    }

    this.api.getAppointmentReport(param).subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res) {
          this.stats = {
            total: res.stats.total || 0,
            checkedIn: res.stats.in_progress || 0,
            completed: res.stats.completed || 0,
            waiting: res.stats.waiting || 0,
            cancelled: res.stats.cancelled || 0,
            noShow: res.stats.no_show || 0
          };
          this.doctorWiseData = res.byDoctor || [];
          this.centerWiseData = res.byCenter || [];
          this.trendData = res.trend || [];
          this.typeData = res.byType || [];

          // Render appropriate chart for active tab
          this.selectTab(this.activeTab);
        }
      },
      error: () => { this.loading = false; }
    });
  }

  renderStatusChart() {
    const canvas = this.statusChartCanvas?.nativeElement;
    if (!canvas) return;
    this.statusChart?.destroy();
    this.statusChart = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: ['In Progress', 'Completed', 'Waiting', 'Cancelled', 'No Show'],
        datasets: [{
          data: [this.stats.checkedIn, this.stats.completed, this.stats.waiting, this.stats.cancelled, this.stats.noShow],
          backgroundColor: ['#3b82f6','#22c55e','#f59e0b','#ef4444','#8b5cf6'],
          borderWidth: 3,
          borderColor: '#fff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '68%',
        plugins: {
          legend: { position: 'bottom', labels: { padding: 14, font: { size: 12 }, usePointStyle: true } },
          tooltip: { callbacks: { label: (ctx: any) => ` ${ctx.label}: ${ctx.parsed} (${this.stats.total > 0 ? ((ctx.parsed / this.stats.total) * 100).toFixed(1) : 0}%)` } }
        }
      }
    });
  }

  renderTrendChart() {
    const canvas = this.trendChartCanvas?.nativeElement;
    if (!canvas) return;
    this.trendChart?.destroy();

    const labels = this.trendData.map((item: any) => moment(item.date).format('DD MMM'));
    const values = this.trendData.map((item: any) => item.count);

    this.trendChart = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: 'Appointments',
          data: values,
          borderColor: '#1b6281',
          backgroundColor: 'rgba(27, 98, 129, 0.08)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#1b6281',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 5,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false }, ticks: { maxRotation: 45, font: { size: 11 } } },
          y: { beginAtZero: true, grid: { color: '#f1f5f9' }, ticks: { stepSize: 1, font: { size: 11 } } }
        }
      }
    });
  }

  renderTypeChart() {
    const canvas = this.typeChartCanvas?.nativeElement;
    if (!canvas) return;
    this.typeChart?.destroy();

    const labels = this.typeData.map((item: any) => item.type.replace(/_/g,' ').replace(/\b\w/g, (c: string) => c.toUpperCase()));
    const values = this.typeData.map((item: any) => item.count);
    const palette = ['#1b6281', '#0d4259', '#378cae', '#67b3d1', '#98dae4'];

    this.typeChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label: 'Count',
          data: values,
          backgroundColor: labels.map((_: any, i: number) => palette[i % palette.length]),
          borderRadius: 6,
          borderSkipped: false,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false }, ticks: { font: { size: 11 } } },
          y: { beginAtZero: true, grid: { color: '#f1f5f9' }, ticks: { stepSize: 1, font: { size: 11 } } }
        }
      }
    });
  }

  renderDoctorChart() {
    const canvas = this.doctorChartCanvas?.nativeElement;
    if (!canvas) return;
    this.doctorChart?.destroy();

    const labels = this.doctorWiseData.map((d: any) => d.name);
    const values = this.doctorWiseData.map((d: any) => d.total);
    const palette = ['#1b6281', '#0d4259', '#378cae', '#67b3d1', '#98dae4'];

    this.doctorChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label: 'Total Appointments',
          data: values,
          backgroundColor: labels.map((_: any, i: number) => palette[i % palette.length]),
          borderRadius: 6,
          borderSkipped: false,
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { beginAtZero: true, grid: { color: '#f1f5f9' }, ticks: { stepSize: 1, font: { size: 11 } } },
          y: { grid: { display: false }, ticks: { font: { size: 11 } } }
        }
      }
    });
  }

  renderCenterChart() {
    const canvas = this.centerChartCanvas?.nativeElement;
    if (!canvas) return;
    this.centerChart?.destroy();

    const labels = this.centerWiseData.map((c: any) => c.name);
    const values = this.centerWiseData.map((c: any) => c.total);
    const palette = ['#1b6281', '#0d4259', '#378cae', '#67b3d1', '#98dae4'];

    this.centerChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label: 'Total Appointments',
          data: values,
          backgroundColor: labels.map((_: any, i: number) => palette[i % palette.length]),
          borderRadius: 6,
          borderSkipped: false,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false }, ticks: { font: { size: 11 } } },
          y: { beginAtZero: true, grid: { color: '#f1f5f9' }, ticks: { stepSize: 1, font: { size: 11 } } }
        }
      }
    });
  }

  getPresetLabel(): string {
    if (this.isCustomRange) return `${moment(this.dateRange.from).format('DD MMM')} - ${moment(this.dateRange.to).format('DD MMM YYYY')}`;
    return DATE_PRESETS.find(p => p.value === this.selectedPreset)?.label || 'Custom';
  }

  pct(n: number): string {
    return this.stats.total > 0 ? ((n / this.stats.total) * 100).toFixed(1) : '0';
  }
}
